import React, { useMemo } from 'react'

import { DoMenus_GetMenuItemsItem as DoMenusGetMenuItemsItem } from 'src/apollo/onlineOrdering'

import { GroupItemBackupContent } from './types'
import Link from 'shared/components/common/link'
import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'
import {
  getMenuPath,
  getOrderPath
} from 'shared/components/common/restaurant_routes/RestaurantRoutesContext'

import {
  menuItemPagePath,
  menuItemPath
} from 'public/components/default_template/menu_section/MenuItemCard'

import { GroupedMenuItemsProps } from './GroupedMenuItems'
import MenuItem from './MenuItem'

type LinkedItemProps = {
  item: DoMenusGetMenuItemsItem | GroupItemBackupContent
} & GroupedMenuItemsProps

// A menu item that is designed to live inside a MenuGroup block that links to the OO page.
export const LinkedItem = (props: LinkedItemProps) => {
  const { item, groupGuid } = props
  const { restaurant, locations } = useRestaurant()
  const hasOoPage =
    restaurant.config?.isOnlineOrderingEnabled &&
    !restaurant.config.ooConfig?.optedOut

  const linkHref = useMemo(() => {
    if (!hasOoPage || !locations) {
      return null
    } else if (
      locations?.length === 1 &&
      locations[0]?.shortUrl &&
      item.guid &&
      item.name
    ) {
      // groupGuid is the top-level menu group guid which breaks items in sub-groups
      // itemGroupGuid is the guid of the sub-group that the item belongs to OR the top-level menu group guid if the item is not in a sub-group
      // fallback to groupGuid if itemGroupGuid is not present(shouldnt happen but keeping it to be safe)
      const itemGroupGuid = item?.itemGroupGuid || groupGuid
      // If there is just one location, link directly to the item.
      return itemGroupGuid
        ? menuItemPagePath(
            getMenuPath(locations[0]?.shortUrl),
            itemGroupGuid,
            item?.name,
            item?.guid
          )
        : menuItemPath(
            getOrderPath(locations[0]?.shortUrl),
            item.name,
            item.guid
          )
    } else {
      // If there is more than one location, don't assume which location the user
      // would like to see. Fallback to the order page if there is a data issue.
      return '/order'
    }
  }, [
    groupGuid,
    hasOoPage,
    item.itemGroupGuid,
    item.guid,
    item.name,
    locations
  ])

  const itemComponent = useMemo(() => {
    return <MenuItem {...props} />
  }, [props])

  return linkHref ? (
    <Link href={linkHref} key={item.guid}>
      {itemComponent}
    </Link>
  ) : (
    <div>{itemComponent}</div>
  )
}

export default LinkedItem
